/**
 * Add Coverage Notes section to an existing worksheet
 */

import ExcelJS from "exceljs";

/**
 * Adds a coverage notes section to an existing worksheet
 *
 * @param worksheet - ExcelJS worksheet to add notes to
 * @param noteContent - The note content to add
 *
 * @example
 * ```ts
 * const worksheet = workbook.addWorksheet("Dental");
 * // ... add benefits, rates, summary sections ...
 * if (noteContent) {
 *   addCoverageNotesToWorksheet(worksheet, noteContent);
 * }
 * ```
 */
const LINE_HEIGHT_PTS = 15;

const calcNoteRowHeight = (content: string, columnWidth: number): number => {
  // ExcelJS column width units ≈ 1.25 chars per unit in Calibri 11pt
  const charsPerLine = Math.round(columnWidth * 1.25);
  const lines = content.split("\n");
  const totalLines = lines.reduce(
    (acc, line) => acc + Math.max(1, Math.ceil(line.length / charsPerLine)),
    0,
  );
  return Math.max(LINE_HEIGHT_PTS, totalLines * LINE_HEIGHT_PTS);
};

export const addCoverageNotesToWorksheet = (
  worksheet: ExcelJS.Worksheet,
  noteContent: string,
): void => {
  if (!noteContent || noteContent.trim() === "") {
    return;
  }

  // Add spacing row before notes section
  worksheet.addRow([]);

  const headerRow = worksheet.addRow(["Coverage Notes"]);
  headerRow.font = { bold: true, size: 14 };
  headerRow.alignment = { horizontal: "left", vertical: "middle" };

  const column = worksheet.getColumn(1);
  if (!column.width || column.width < 100) {
    column.width = 100;
  }

  const contentRow = worksheet.addRow([noteContent]);
  contentRow.alignment = {
    horizontal: "left",
    vertical: "top",
    wrapText: true,
  };
  contentRow.height = calcNoteRowHeight(noteContent, column.width ?? 100);
};
